import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  MessageCircle, HeartHandshake, Calendar, Sparkles, ShieldCheck,
  Clock, Phone, BookOpen,
} from "lucide-react";
import useSeo, { PROD_DOMAIN } from "../lib/seo";
import { Button } from "../components/ui/button";
import {
  TrustPage, TrustHero, TrustSection, IconCardGrid,
  ClosingCta, WhatsAppInline,
} from "../components/TrustLayout";
import { api } from "../lib/api";

/**
 * /mentor-support — calm "you're not alone" page. CTAs to WhatsApp,
 * assessment and contact form.
 */
export default function MentorSupportPage() {
  const [whatsapp, setWhatsapp] = useState("447424225333");
  useEffect(() => {
    api.get("/config").then(({ data }) => setWhatsapp(data?.whatsapp_number || "447424225333")).catch(() => {});
  }, []);

  useSeo({
    title: "Wellness mentor support — Feel Greats",
    description:
      "You're not doing this alone. Free WhatsApp guidance from a real UK-based wellness mentor — before, during and after your wellness journey.",
    canonical: `${PROD_DOMAIN}/mentor-support`,
    ogType: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Feel Greats wellness mentor support",
        "url": `${PROD_DOMAIN}/mentor-support`,
        "areaServed": "Worldwide",
        "provider": {
          "@type": "Organization",
          "name": "Feel Greats",
          "url": PROD_DOMAIN,
        },
        "serviceType": "Wellness guidance and personal mentoring",
        "availableChannel": {
          "@type": "ServiceChannel",
          "name": "WhatsApp",
          "availableLanguage": ["en"],
        },
      },
    ],
  });

  return (
    <TrustPage testid="mentor-page">
      <TrustHero
        eyebrow="Mentor support"
        headline="You're not doing this alone."
        highlightWord="alone"
        body="Wellness is rarely about the product — it's about whether someone shows up consistently for you. Our wellness mentors do. Before you've bought anything. After every order. Quarterly check-ins. Always free."
        primaryCta={{ href: `https://wa.me/${whatsapp}?text=Hi%20Feel%20Greats%20team!%20I%27d%20love%20some%20guidance.`, label: "Chat to a mentor on WhatsApp", external: true, testid: "mentor-hero-whatsapp", icon: MessageCircle }}
        secondaryCta={{ to: "/questionnaire", label: "Take the assessment first", testid: "mentor-hero-assessment" }}
        testid="mentor-hero"
      />

      {/* What mentor support actually is */}
      <TrustSection
        eyebrow="What you get"
        title="Real human guidance, all free."
        body="Our mentors are UK-based certified wellness practitioners. They don't earn commission on what you buy — they earn their salary by helping people feel better, regardless of order size."
        tone="default"
        testid="mentor-what"
      >
        <IconCardGrid
          cols={3}
          cards={[
            {
              icon: MessageCircle,
              eyebrow: "Anytime",
              title: "WhatsApp guidance.",
              body: "A short message gets a personal reply (not a chatbot) during UK working hours. Ask about your assessment, your ingredients, your routine, or whether to start at all.",
            },
            {
              icon: Calendar,
              eyebrow: "Optional",
              title: "Scheduled check-ins.",
              body: "Week 2, week 6, and quarterly nudges. We track them per member so you never get spammed — and you can mute them with one tap.",
            },
            {
              icon: HeartHandshake,
              eyebrow: "Always",
              title: "Accountability that doesn't shame.",
              body: "If you've slipped from your rhythm, we nudge gently. If you've crushed it, we celebrate quietly. We're not your trainer — we're the friend who happens to know wellness deeply.",
            },
          ]}
        />
      </TrustSection>

      {/* Calm reassurance — what mentor support is NOT */}
      <TrustSection
        eyebrow="What it isn't"
        title="No pressure. Never a sales pitch."
        body="Our mentors are explicitly trained to never push a product. If your assessment suggests our products aren't a fit — or that you should try a habit before a supplement — they'll say so."
        tone="amber"
        testid="mentor-not"
      >
        <IconCardGrid
          cols={2}
          cards={[
            { icon: ShieldCheck, title: "Not medical advice.", body: "We're a wellness service, not clinicians. If you're pregnant, nursing, on medication, or managing a chronic condition, we'll route you to a GP — and pause any product suggestion." },
            { icon: Phone, title: "Not a call centre.", body: "There's no script. Each mentor knows your assessment and your history. The reply is always personal." },
            { icon: BookOpen, title: "Not a one-time chat.", body: "If you'd like, your mentor stays with you. The same person who replied in week 1 still knows your story in month 6." },
            { icon: Sparkles, title: "Not optional only at the start.", body: "Members message us months in. Some never buy anything and still get guidance. That's the deal." },
          ]}
        />
      </TrustSection>

      {/* CTAs */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-testid="mentor-cta-grid">
        <p className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 font-semibold">How to reach us</p>
        <h2 className="font-display text-3xl sm:text-4xl text-emerald-950 font-light leading-tight mt-2 max-w-3xl">
          Pick the way that feels easiest.
        </h2>
        <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <CtaCard
            icon={MessageCircle}
            title="Chat on WhatsApp"
            body="Quickest. Most members message a question before they ever buy. No commitment."
            cta={{ label: "Open WhatsApp", href: `https://wa.me/${whatsapp}?text=Hi%2C%20I%27d%20love%20a%20wellness%20mentor%27s%20guidance.`, external: true, testid: "mentor-cta-whatsapp" }}
            time="Live · UK working hours"
          />
          <CtaCard
            icon={Sparkles}
            title="Take the assessment"
            body="Two minutes, no card. Your assessment becomes the canvas your mentor works from."
            cta={{ label: "Start the assessment", to: "/questionnaire", testid: "mentor-cta-assessment" }}
            time="Free · Always available"
          />
          <CtaCard
            icon={Clock}
            title="Ask a question by email"
            body="Slower, but written replies often go deeper. Best for ingredient questions or longer history."
            cta={{ label: "support@feelgreats.co.uk", href: "mailto:support@feelgreats.co.uk", testid: "mentor-cta-email" }}
            time="Reply within 1 business day"
          />
        </div>
      </section>

      <TrustSection
        eyebrow="A small note"
        title="Why mentor support is free."
        body="Because we don't believe wellness guidance should be locked behind a paywall. We make our small margin on the products we recommend — and we accept that some of you will never buy anything. That's fine. The math works either way because we don't burn money on ads — we'd rather invest it in the mentor team."
        tone="white"
        testid="mentor-note"
      />

      <ClosingCta
        eyebrow="One tap away"
        title="Send your first message."
        body="No script, no commitment. Just say what's on your mind — sleep, energy, weight, anxiety, anything. We'll meet you where you are."
      />
    </TrustPage>
  );
}

function CtaCard({ icon: Icon, title, body, cta, time }) {
  const inner = (
    <article className="rounded-3xl border border-emerald-100 bg-white p-6 h-full flex flex-col hover:border-emerald-300 hover:shadow-sm transition">
      <span className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-700 mb-3">
        <Icon className="w-4 h-4" strokeWidth={1.6} />
      </span>
      <h3 className="font-display text-lg text-emerald-950 font-medium leading-snug">{title}</h3>
      <p className="text-[13.5px] text-emerald-900/75 mt-2 leading-relaxed flex-1">{body}</p>
      {time && (
        <p className="text-[11px] text-emerald-700/80 mt-3 inline-flex items-center"><Clock className="w-3 h-3 mr-1" /> {time}</p>
      )}
      <Button className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white mt-4 self-start" data-testid={cta.testid}>
        {cta.label}
      </Button>
    </article>
  );
  if (cta.to) return <Link to={cta.to}>{inner}</Link>;
  return cta.external ? (
    <a href={cta.href} target="_blank" rel="noopener noreferrer">{inner}</a>
  ) : (
    <a href={cta.href}>{inner}</a>
  );
}
